/**
 * 将十六进制颜色转换为 rgba 格式，并注入到 CSS 变量中
 * @param {string} hexColor - 十六进制颜色值，如 "#1890ff" 或 "1890ff"
 * @param {string} [cssVarName='lottery-theme-color'] - CSS 变量名前缀，会生成 -rgb 后缀的变量
 * @returns {object|null} 返回 RGB 对象 {r, g, b} 和对应的 CSS 变量值
 */
export function setThemeColor(hexColor, cssVarName = 'lottery-theme-color') {
    try {
        // 处理空值情况
        if (!hexColor) {
            console.warn('setThemeColor: hexColor 为空，跳过设置');
            return null;
        }
        
        // 清除 # 号
        let hex = hexColor.replace(/^#/, '');
        
        // 处理简写形式 #fff
        if (hex.length === 3) {
            hex = hex.split('').map(c => c + c).join('');
        }
        
        // 验证十六进制格式
        if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
            throw new Error('无效的十六进制颜色格式');
        }
        
        // 转换为 RGB 分量
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        
        // 注入 CSS 变量
        document.documentElement.style.setProperty(`--${cssVarName}`, `#${hex}`);
        document.documentElement.style.setProperty(`--${cssVarName}-rgb`, `${r}, ${g}, ${b}`);
        
        return {
            rgb: { r, g, b },
            hex: `#${hex}`,
            rgba: (opacity = 1) => `rgba(${r}, ${g}, ${b}, ${opacity})`,
            cssVar: `var(--${cssVarName})`,
            cssVarRgb: `var(--${cssVarName}-rgb)`
        };
    } catch (error) {
        console.error('设置主题颜色失败:', error);
        return null;
    }
}

/**
 * 获取当前主题颜色的 RGB 值
 * @param {string} [cssVarName='lottery-theme-color'] - CSS 变量名
 * @returns {object|null} 返回 RGB 对象 {r, g, b}
 */
export function getThemeColor(cssVarName = 'lottery-theme-color') {
    try {
        const rgbStr = getComputedStyle(document.documentElement)
            .getPropertyValue(`--${cssVarName}-rgb`)
            .trim();
        
        if (!rgbStr) return null;
        
        const [r, g, b] = rgbStr.split(',').map(v => parseInt(v.trim(), 10));
        return { r, g, b };
    } catch (error) {
        console.error('获取主题颜色失败:', error);
        return null;
    }
}

/**
 * 将十六进制颜色转换为 RGB 对象
 * @param {string} hexColor - 十六进制颜色值，如 "#1890ff" 或 "1890ff"
 * @returns {object|null} 返回 RGB 对象 {r, g, b}
 */
export function hexToRgb(hexColor) {
    try {
        // 处理空值情况
        if (!hexColor) {
            console.warn('hexToRgb: hexColor 为空');
            return null;
        }
        
        let hex = hexColor.replace(/^#/, '');
        
        if (hex.length === 3) {
            hex = hex.split('').map(c => c + c).join('');
        }
        
        if (!/^[0-9a-fA-F]{6}$/.test(hex)) {
            throw new Error('无效的十六进制颜色格式');
        }
        
        return {
            r: parseInt(hex.substring(0, 2), 16),
            g: parseInt(hex.substring(2, 4), 16),
            b: parseInt(hex.substring(4, 6), 16)
        };
    } catch (error) {
        console.error('十六进制转RGB失败:', error);
        return null;
    }
}

/**
 * 将 RGB 或十六进制颜色转换为 rgba 字符串
 * @param {string|object} color - 颜色值，可以是十六进制字符串或 RGB 对象 {r, g, b}
 * @param {number} opacity - 透明度，范围 0-1
 * @returns {string|null} 返回 rgba 字符串
 */
export function toRgba(color, opacity = 1) {
    try {
        let rgb;
        
        // 如果是字符串，先转为 RGB 对象
        if (typeof color === 'string') {
            rgb = hexToRgb(color);
        } else if (typeof color === 'object' && color.r !== undefined) {
            rgb = color;
        } else {
            throw new Error('无效的颜色格式');
        }
        
        if (!rgb) return null;
        
        // 限制透明度范围
        const alpha = Math.min(1, Math.max(0, opacity));
        
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
    } catch (error) {
        console.error('转换为rgba失败:', error);
        return null;
    }
}